'use client';

import cn from 'classnames';
import { useTranslations } from 'next-intl';

import { Button, Field, Input, Label, Translation } from '@/ui/components';
import type { Spacing } from '@/ui/types';
import { SomethingWrong } from '../SomethingWrong';
import { useLoginFormViewModel } from './useLoginFormViewModel';

import s from './LoginForm.module.css';

type LoginFormProps = {
  spacing?: Spacing;
};

const LoginForm = ({ spacing }: LoginFormProps) => {
  const {
    isSuccess,
    isLoading,
    formTouched,
    formErrors,
    errorType,
    formIsValid,
    formIsDirty,
    setIsSuccess,
    handleResetForm,
    setHasError,
    handleFocus,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useLoginFormViewModel();

  const translation = useTranslations();

  if (isSuccess === false) {
    return (
      <SomethingWrong
        onPrimaryButtonClick={handleResetForm}
        onSecondaryButtonClick={() => setIsSuccess(null)}
      />
    );
  }
  const handleContact = () => {};

  if (errorType === 'USER_NOT_FOUND') {
    return (
      <SomethingWrong
        title={translation('title')}
        description={translation('description')}
        primaryButtonLabel={translation('primary_button')}
        primaryButtonIcon="Wsp"
        secondaryButtonLabel={translation('secondary_button')}
        onPrimaryButtonClick={handleContact}
        onSecondaryButtonClick={() => setHasError(false)}
      />
    );
  }

  return (
    <form className={cn(s.form, { [`spacing-${spacing}`]: !!spacing })} onSubmit={handleSubmit}>
      <Field spacing="medium">
        <Label spacing="micro" htmlFor="email">
          <Translation context="login" token="email_label" />
        </Label>
        <Input
          data-testid="email"
          id="email"
          name="email"
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          hasError={formTouched.email && !!formErrors.email}
          errorMessage={formErrors.email}
        />
      </Field>

      <Field spacing="medium">
        <Label spacing="micro" htmlFor="password">
          <Translation context="login" token="password_label" />
        </Label>
        <Input
          data-testid="password"
          type="password"
          id="password"
          name="password"
          spacing="tiny"
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          hasError={formTouched.password && !!formErrors.password}
          errorMessage={formErrors.password}
        />
      </Field>

      <footer className={s.footer}>
        <Button
          data-testid="login-button"
          type="submit"
          isFullWidth
          disabled={!(formIsValid && formIsDirty) || isLoading}
        >
          <Translation context="login" token="button_primary" />
        </Button>
        <Button variant="link" href="reset">
          <Translation context="login" token="forgot_password_button" />
        </Button>
      </footer>
    </form>
  );
};

export default LoginForm;
